import { Column, Row } from 'src/shared/components/Semantic/all'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useOrderTableCurrency } from '../../currency'
import { Button, Tooltip } from '../../../ui'
import { useNavigate } from 'react-router-dom'
import { paths } from 'src/routes/paths'
import { ArrowRight } from 'lucide-react'
import type { QuoteFooterSubtotals } from '../../utils'
import { Separator } from 'src/shared/components/ui/separator'

const FOOTER_SUBTOTAL_ROWS: {
  key: keyof QuoteFooterSubtotals
  en: string
  sv: string
}[] = [
  { key: 'productBase', en: 'Products', sv: 'Produkter' },
  { key: 'decoration', en: 'Decorations', sv: 'Dekorationer' },
  { key: 'setupCost', en: 'Setup cost', sv: 'Startkostnad' },
  {
    key: 'productCustomLines',
    en: 'Additional lines',
    sv: 'Tilläggsrader',
  },
  { key: 'shipping', en: 'Shipping', sv: 'Frakt' },
  { key: 'customQuoteItems', en: 'Custom items', sv: 'Anpassade rader' },
  { key: 'otherSubItems', en: 'Other', sv: 'Övrigt' },
]

export const OrderTableFooter = ({
  footerSubtotals,
  totalPrice,
  quotePublicId,
  chatView = false,
}: {
  footerSubtotals: QuoteFooterSubtotals
  totalPrice: number
  quotePublicId?: string
  chatView?: boolean
}) => {
  const navigate = useNavigate()
  const { t } = useLocalization()
  const currency = useOrderTableCurrency()

  return (
    <Row className="ml-auto mt-auto justify-end p-2 max-sm:w-full max-sm:ml-0 max-sm:p-0">
      <Column className="w-64 gap-3 max-sm:w-full">
        <Column className="rounded-card border border-border bg-muted/40 px-4 py-3.5 gap-1.5">
          {FOOTER_SUBTOTAL_ROWS.map(({ key, en, sv }) => {
            const amount = footerSubtotals[key]
            if (amount <= 0) return null
            return (
              <Row key={key} className="justify-between items-center gap-2">
                <span className="text-sm text-muted-foreground">{t(en, sv)}</span>
                <span className="text-sm font-medium text-foreground shrink-0 tabular-nums">
                  {amount.toLocaleString('sv-SE')} {currency}
                </span>
              </Row>
            )
          })}

          <Separator className="my-1" />

          <Tooltip
            content={t(
              'All prices are without VAT / tax',
              'Alla priser är exklusive moms / skatt'
            )}
          >
            <div className="row justify-between items-center cursor-help">
              <p className="text-sm font-medium text-foreground">
                {t('Approx. total', 'Uppskattat totalt')}
              </p>

              <p className="text-lg font-semibold text-foreground tabular-nums">
                {totalPrice.toLocaleString('sv-SE')} {currency}
              </p>
            </div>
          </Tooltip>
        </Column>

        {chatView && (
          <Button
            className="w-full"
            onClick={() =>
              navigate(`${paths.myAccount}${paths.requests}/${quotePublicId}`)
            }
          >
            {t('Go to order', 'Gå till order')}
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        )}
      </Column>
    </Row>
  )
}
